<script>
export default {
  props: {
    activeHover: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    tooltip: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "far fa-envelope",
    },
    iconSrc: {
      type: String,
      default: "",
    },
    expandOnHover: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tag: {
      type: String,
      default: "b-button",
    },
  },
  computed: {
    isNativeButton() {
      return this.tag === "button" || this.tag === "b-button";
    },
    isCustomButton() {
      return !this.isNativeButton;
    },
    element() {
      return this.isNativeButton ? "button" : this.tag;
    },
    elementAttrs() {
      if (this.isNativeButton) return { type: "button" };

      return { role: "button", tabindex: 0 };
    },
    tooltipText() {
      return this.tooltip || (this.disabled ? this.title : "");
    },
    buttonAriaLabel() {
      return (
        this.label || this.title || (this.disabled ? this.tooltipText : null)
      );
    },
    disabledReason() {
      if (!this.disabled || this.tooltipText === this.buttonAriaLabel) {
        return "";
      }

      return this.tooltipText;
    },
    disabledReasonId() {
      return this.disabledReason ? `sales-button-reason-${this._uid}` : null;
    },
  },
  methods: {
    onClick() {
      if (this.disabled) return;
      this.$emit("click");
    },
    onKeyActivate(event) {
      if (!this.isCustomButton) return;

      event.preventDefault();
      this.onClick();
    },
  },
};
</script>

<template>
  <div
    class="sales-button"
    :class="{ 'sales-button--static': !expandOnHover }"
    v-b-tooltip.hover.focus="tooltipText"
  >
    <component
      :is="element"
      class="sales-button__button"
      :class="{
        'btn btn-outline-primary': isNativeButton,
        'sales-button__active': activeHover,
        'sales-button__disabled': disabled,
      }"
      :title="disabled ? null : title"
      :aria-label="buttonAriaLabel"
      :aria-disabled="disabled ? 'true' : null"
      :aria-describedby="disabledReasonId"
      v-bind="elementAttrs"
      @click="onClick"
      @keydown.enter="onKeyActivate"
      @keydown.space="onKeyActivate"
    >
      <img
        v-if="iconSrc"
        class="sales-button__icon sales-button__icon--image"
        :src="iconSrc"
        alt=""
      >
      <i v-else :class="icon"></i>
      <span class="sales-button__label" aria-hidden="true">{{ label }}</span>
      <span v-if="disabledReason" :id="disabledReasonId" class="sr-only">
        {{ disabledReason }}
      </span>
    </component>
  </div>
</template>

<style lang="scss">
.sales-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  &__button:focus-visible {
    outline: 2px solid #000c0b;
    outline-offset: 2px;
  }

  &__disabled {
    color: inherit !important;
    pointer-events: none;
  }

  &:hover {
    .sales-button__label {
      display: flex;
      opacity: 1;
      transform: translateX(0);
    }

    .sales-button__button {
      background-color: #000c0b !important;
      color: #fff;
      border: 0;
    }
  }

  &:hover .sales-button__button.sales-button__disabled {
    background-color: #f0f2f5 !important;
    color: inherit !important;
    border-color: #f0f2f5 !important;
  }

  &:hover .sales-button__button.sales-button__disabled .sales-button__label {
    display: none;
    opacity: 0;
    transform: translateX(-6px);
  }

  &--static:hover .sales-button__label {
    display: none;
    opacity: 0;
    transform: translateX(-6px);
  }

  &__button {
    justify-content: center;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #000c0b;
    border-color: #f0f2f5 !important;
    border-radius: 8px;
    padding: 14px;
    box-shadow: none !important;
    background-color: #f0f2f5 !important;
    width: fit-content;
    min-width: 44px;
    height: 44px;
  }

  &__icon--image {
    width: 17px;
    height: 17px;
  }

  &:hover &__icon--image {
    filter: brightness(0) invert(1);
  }

  &:hover .sales-button__button.sales-button__disabled &__icon--image {
    filter: none;
  }

  &__active {
    display: flex;
    gap: 0.5rem;
    background-color: #000c0b !important;
    box-shadow: none !important;
    border-color: #000c0b !important;
    color: #fff;
    border: 0;

    .sales-button__label {
      display: flex !important;
    }
  }

  &__label {
    display: none;
    transform: translateX(-6px);
    transition: opacity 0.25s ease, transform 0.25s ease;
    white-space: nowrap;
    font-size: 0.9rem;
  }
}
</style>
